import { useState, useEffect, useMemo } from 'react';
import { Search } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { listAuditLogs, getAuditStats } from '../../../entities/audit';

export function AuditLogsPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  const [actionFilter, setActionFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('today');

  const [stats, setStats] = useState(null);

  // Debounce
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(id);
  }, [search]);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    p.set('page', String(page));
    p.set('limit', String(limit));
    if (debouncedSearch.trim()) p.set('q', debouncedSearch.trim());
    if (actionFilter !== 'all') p.set('action', actionFilter);
    if (severityFilter !== 'all') p.set('severity', severityFilter);
    if (periodFilter !== 'all') p.set('period', periodFilter);
    p.set('sortBy', 'created_at');
    p.set('sortDir', 'desc');
    return `?${p.toString()}`;
  }, [page, debouncedSearch, actionFilter, severityFilter, periodFilter]);

  useEffect(() => {
    setLoading(true);
    listAuditLogs(queryString)
      .then(res => {
        setLogs(res.data || []);
        setTotal(res.total || 0);
        setTotalPages(Math.ceil((res.total || 0) / limit));
      })
      .catch(() => toast.error('Failed to load audit logs'))
      .finally(() => setLoading(false));
  }, [queryString]);

  useEffect(() => {
    getAuditStats()
      .then(setStats)
      .catch(() => {});
  }, []);

  const severityColors = {
    Info: 'bg-blue-100 text-blue-700',
    Warning: 'bg-yellow-100 text-yellow-700',
    Error: 'bg-red-100 text-red-700',
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  };

  return (
    <div className="p-6 w-full space-y-6">
      {/* Top Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 text-center">
          <p className="text-sm text-gray-500">Total Activities</p>
          <h3 className="text-xl font-semibold">{stats?.totalActivities ?? '-'}</h3>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 text-center">
          <p className="text-sm text-gray-500">Active Users</p>
          <h3 className="text-xl font-semibold">{stats?.activeUsers ?? '-'}</h3>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 text-center">
          <p className="text-sm text-gray-500">Today's Events</p>
          <h3 className="text-xl font-semibold">{stats?.todayEvents ?? '-'}</h3>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 text-center">
          <p className="text-sm text-gray-500">Warnings</p>
          <h3 className="text-xl font-semibold text-red-600">{stats?.warnings ?? '-'}</h3>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl shadow-sm p-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            className="pl-9 pr-3 py-2 rounded-lg border border-gray-300 bg-white text-sm w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select value={actionFilter} onChange={(e) => { setPage(1); setActionFilter(e.target.value); }} className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm">
          <option value="all">All Actions</option>
          <option value="CREATE LOAD">Create Load</option>
          <option value="UPDATE LOAD">Update Load</option>
          <option value="CREATE INVOICE">Create Invoice</option>
          <option value="CREATE USER">Create User</option>
          <option value="LOGIN SUCCESS">Login Success</option>
          <option value="FAILED LOGIN">Failed Login</option>
        </select>
        <select value={severityFilter} onChange={(e) => { setPage(1); setSeverityFilter(e.target.value); }} className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm">
          <option value="all">All Levels</option>
          <option value="Info">Info</option>
          <option value="Warning">Warning</option>
          <option value="Error">Error</option>
        </select>
        <select value={periodFilter} onChange={(e) => { setPage(1); setPeriodFilter(e.target.value); }} className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm">
          <option value="today">Today</option>
          <option value="week">This Week</option>
          <option value="all">All Time</option>
        </select>
      </div>

      {/* Activity Log Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <h3 className="text-lg font-medium p-4 border-b">Activity Log</h3>
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading audit logs...</div>
        ) : (
          <>
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-600 font-medium">
                <tr>
                  <th className="px-6 py-3">Timestamp</th>
                  <th className="px-6 py-3">User</th>
                  <th className="px-6 py-3">Action</th>
                  <th className="px-6 py-3">Resource</th>
                  <th className="px-6 py-3">Details</th>
                  <th className="px-6 py-3">Severity</th>
                  <th className="px-6 py-3">IP Address</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 && (
                  <tr><td colSpan={7} className="px-6 py-8 text-center text-gray-500">No audit logs found.</td></tr>
                )}
                {logs.map((log) => (
                  <tr key={log.id} className="border-t">
                    <td className="px-6 py-3 text-gray-700">{formatTimestamp(log.created_at)}</td>
                    <td className="px-6 py-3 text-gray-700">{log.user_name || '-'}</td>
                    <td className="px-6 py-3 font-medium text-gray-800">{log.action}</td>
                    <td className="px-6 py-3">
                      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">{log.resource || '-'}</span>
                    </td>
                    <td className="px-6 py-3 text-gray-600">{log.details || '-'}</td>
                    <td className="px-6 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${severityColors[log.severity] || ''}`}>{log.severity}</span>
                    </td>
                    <td className="px-6 py-3 text-gray-600">{log.ip_address || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t">
              <span className="text-sm text-gray-500">
                Showing <span className="font-medium">{logs.length === 0 ? 0 : (page - 1) * limit + 1}-{Math.min(page * limit, total)}</span> of {total}
              </span>
              <div className="flex items-center gap-1">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 py-1 text-sm rounded hover:bg-gray-200 disabled:opacity-50">&lt;</button>
                <span className="px-3 py-1 text-sm rounded bg-blue-600 text-white">{page}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-2 py-1 text-sm rounded hover:bg-gray-200 disabled:opacity-50">&gt;</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
